// One way to ask for recommendations, full routine or specific products.
//
// The results screen and the "what next" screen both need this. It saves the
// skin profile, sends a signed-out visitor through sign-up (the pending
// request is picked up by App.svelte after the redirect, exactly as before),
// then fetches, stores and navigates.

import { get } from 'svelte/store';
import { recommendationsStore, userStore } from './store.js';
import { formDataStore } from './components/store';
import { navigate } from './router.js';
import { engineProfile } from './profile.js';

export function storedProfile() {
    try {
        return JSON.parse(localStorage.getItem('skinProfile') || 'null') || get(formDataStore) || {};
    } catch (e) {
        return get(formDataStore) || {};
    }
}

export function storedRoutine() {
    try {
        return JSON.parse(localStorage.getItem('currentRoutine') || '[]') || [];
    } catch (e) {
        return [];
    }
}

// What the engine needs to know about each routine product to check a
// recommendation against it.
export function routineForEngine(items) {
    return (items || []).map(item => ({
        productId: item.productId || null,
        productName: item.productName,
        brand: item.brand || '',
        productType: item.productType || '',
        timing: item.timing || 'both',
        prescribed: !!item.prescribed,
        notes: item.notes || ''
    }));
}

/**
 * @param {object}   options
 * @param {string[]} [options.productTypes]  specific-products mode; omit for a full routine
 * @returns {Promise<{ok: boolean, redirected?: boolean, error?: string}>}
 */
export async function requestRecommendations({ productTypes = null } = {}) {
    const profile = { ...storedProfile() };
    delete profile.productTypes;
    delete profile.currentRoutine;
    profile.userId = profile.userId || get(userStore).userId || localStorage.getItem('UserId');

    const routine = routineForEngine(storedRoutine());
    const body = engineProfile(profile);
    if (productTypes && productTypes.length) body.productTypes = productTypes;
    if (routine.length) body.currentRoutine = routine;

    try {
        await fetch('/api/save_user_data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(profile)
        });
    } catch (e) {
        console.error('Profile save before recommendations failed:', e);
    }

    const auth = await fetch('/api/is_authenticated', { credentials: 'include' }).catch(() => null);
    const isLocalDev = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (!(auth && auth.ok) && !isLocalDev) {
        localStorage.setItem('pendingRecommendationRequest', 'true');
        localStorage.setItem('formDataForRecommendations', JSON.stringify(body));
        window.location.href = `/api/signup?currentPage=recommendations&oldUserId=${encodeURIComponent(profile.userId || '')}`;
        return { ok: true, redirected: true };
    }

    const response = await fetch('/api/recommendation_engine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });
    if (!response.ok) return { ok: false, error: 'Unable to load recommendations right now.' };

    const recommendations = await response.json();
    // The recommendations screen re-requests with this when filters change,
    // so it has to carry the mode too.
    formDataStore.set(body);
    fetch('/api/store_recommendations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: profile.userId, recommendations, formData: body })
    }).catch(() => {});

    recommendationsStore.set(recommendations);
    localStorage.setItem('activeRoutineRecommendations', JSON.stringify(recommendations));
    navigate('recommendations');
    return { ok: true };
}
